import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_OPTIONS = [
  "Complete Rooftop System",
  "Solar Panels",
  "Smart Inverter",
  "Battery Storage",
  "Solar Water Heater",
  "Solar Lights",
  "Not sure — need advice",
];

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  property_type: "Residential",
  product_interest: PRODUCT_OPTIONS[0],
  city: "",
  monthly_bill: "",
  message: "",
};

export const QuoteForm = ({ preselected, designMessage }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (preselected) setForm((f) => ({ ...f, product_interest: preselected }));
  }, [preselected]);

  useEffect(() => {
    if (designMessage) setForm((f) => ({ ...f, message: designMessage }));
  }, [designMessage]);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your full name";
    if (!/^\+?[\d\s-]{10,16}$/.test(form.phone.trim())) er.phone = "Enter a valid 10-digit phone number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) er.email = "Enter a valid email address";
    if (form.city.trim().length < 2) er.city = "Please enter your city";
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Enquiry received", {
        description: "Our solar expert will call you within 24 hours.",
      });
      setForm({ ...EMPTY });
      setTimeout(() => setDone(false), 6000);
    } catch (err) {
      toast.error("Could not submit", {
        description: err?.response?.data?.detail || "Please try again in a moment.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" className="relative overflow-hidden py-24 lg:py-32">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-1/3 left-[-10%] h-[500px] w-[500px] rounded-full bg-gold/10 blur-[130px]"
      />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="eyebrow">06 — Free Quote</p>
            <h2 className="mt-4 font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.08]">
              Your free solar quote,
              <br />
              <span className="text-gold-grad">in under 24 hours.</span>
            </h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-mutegray">
              Tell us a little about your home or business. A dedicated solar expert will call you
              with a system design, exact pricing and your subsidy eligibility — no spam, no obligation.
            </p>

            <div className="mt-10 space-y-4" data-testid="quote-contact-details">
              {[
                { icon: Phone, label: "Call us", value: "+91 79832 17276" },
                { icon: Mail, label: "Email", value: "anuragsingh@maasakumbrisolar.com" },
                { icon: MapPin, label: "Visit", value: "Rajpur Chhajpur, Tehsil Budhana, Distt. Muzaffarnagar — 247775" },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 bg-gold/[0.07]">
                    <c.icon className="h-4.5 w-4.5 h-5 w-5 text-gold" />
                  </span>
                  <div>
                    <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-mutegray">{c.label}</p>
                    <p className="text-sm font-semibold text-warm">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            {done && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-[1.6rem] border border-gold/40 bg-ink/95 text-center"
                data-testid="quote-success-panel"
              >
                <CheckCircle2 className="h-14 w-14 text-gold" />
                <p className="mt-5 font-display text-2xl font-bold text-warm">Enquiry received!</p>
                <p className="mt-2 max-w-xs text-sm text-mutegray">
                  Our solar expert will call you within 24 hours with your free quote.
                </p>
              </motion.div>
            )}

            <form
              onSubmit={submit}
              noValidate
              data-testid="quote-form"
              className="rounded-[1.6rem] border border-gold/20 bg-surface p-6 sm:p-9"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-[10px] tracking-[0.2em] uppercase text-mutegray" htmlFor="qf-name">Full name *</label>
                  <input id="qf-name" className="field" placeholder="Ramesh Kumar" value={form.name} onChange={set("name")} data-testid="quote-input-name" />
                  {errors.name && <p className="mt-1.5 text-xs text-red-400" data-testid="quote-error-name">{errors.name}</p>}
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] tracking-[0.2em] uppercase text-mutegray" htmlFor="qf-phone">Phone *</label>
                  <input id="qf-phone" className="field" placeholder="98765 43210" inputMode="tel" value={form.phone} onChange={set("phone")} data-testid="quote-input-phone" />
                  {errors.phone && <p className="mt-1.5 text-xs text-red-400" data-testid="quote-error-phone">{errors.phone}</p>}
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] tracking-[0.2em] uppercase text-mutegray" htmlFor="qf-email">Email *</label>
                  <input id="qf-email" className="field" type="email" placeholder="you@example.com" value={form.email} onChange={set("email")} data-testid="quote-input-email" />
                  {errors.email && <p className="mt-1.5 text-xs text-red-400" data-testid="quote-error-email">{errors.email}</p>}
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] tracking-[0.2em] uppercase text-mutegray" htmlFor="qf-city">City *</label>
                  <input id="qf-city" className="field" placeholder="Your city" value={form.city} onChange={set("city")} data-testid="quote-input-city" />
                  {errors.city && <p className="mt-1.5 text-xs text-red-400" data-testid="quote-error-city">{errors.city}</p>}
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] tracking-[0.2em] uppercase text-mutegray" htmlFor="qf-property">Property type</label>
                  <select id="qf-property" className="field" value={form.property_type} onChange={set("property_type")} data-testid="quote-input-property">
                    {["Residential", "Commercial", "Industrial", "Housing Society"].map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] tracking-[0.2em] uppercase text-mutegray" htmlFor="qf-product">Interested in</label>
                  <select id="qf-product" className="field" value={form.product_interest} onChange={set("product_interest")} data-testid="quote-input-product">
                    {PRODUCT_OPTIONS.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block font-mono text-[10px] tracking-[0.2em] uppercase text-mutegray" htmlFor="qf-bill">Avg. monthly bill (optional)</label>
                  <input id="qf-bill" className="field" placeholder="e.g. ₹3,500" value={form.monthly_bill} onChange={set("monthly_bill")} data-testid="quote-input-bill" />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block font-mono text-[10px] tracking-[0.2em] uppercase text-mutegray" htmlFor="qf-message">Anything else?</label>
                  <textarea id="qf-message" className="field min-h-[88px] resize-y" placeholder="Roof size, number of floors, timeline…" value={form.message} onChange={set("message")} data-testid="quote-input-message" />
                </div>
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="quote-submit"
                className="group mt-7 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-8 py-4 text-sm font-bold text-ink transition-all duration-300 hover:bg-goldlight hover:shadow-[0_0_40px_rgba(245,158,11,0.45)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Get my free quote
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </button>
              <p className="mt-4 text-center text-[11px] text-mutegray">
                100% free · No obligation · Response within 24 hours
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
